import React, { useState, useMemo, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Legend } from "recharts";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import AxiosInstance from "@/utils/AxiosInstance";
import {
  getPromise,
  getPromiseToast,
  getReportDateRange,
  type ReportDatePreset,
} from "@/utils/helpers";
import { formatDate, formatDateTime12 } from "@/utils/helpers";
import moment from "moment";
import TakeReadingPopup from "./TakeReadingPopup";
import { useAuth } from "@/contexts/use-auth";
import { Separator } from "@radix-ui/react-select";
import ClientPaginatedMyTable from "@/components/ClientPaginatedMyTable";
import type { DateRange } from "react-day-picker";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CalendarDays } from "lucide-react";
import { isAfter, startOfDay } from "date-fns";
import { useLocation } from "react-router";

const PRESET_OPTIONS: { value: ReportDatePreset; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last7", label: "Last 7 Days" },
  { value: "last15", label: "Last 15 Days" },
  { value: "last30", label: "Last 30 Days" },
  { value: "last90", label: "Last 90 Days" },
  { value: "custom", label: "Custom date range" },
];

interface Reading {
  _id: string;
  readingDate: string;
  readingDateLocal?: string;
  systolic?: number;
  diastolic?: number;
  pulse?: number;
  status: string;
  isAbnormal: boolean;
  [key: string]: any;
}

interface BloodPressureReadingsProps {
  readings: Reading[];
  loading: boolean;
  error: string | null;
  patientId?: string;
  patient?: any;
  onReadingAdded?: () => void;
  onDateRangeChange?: (range: { startDate: string; endDate: string }) => void;
}

const BloodPressureReadings: React.FC<BloodPressureReadingsProps> = ({
  readings,
  loading,
  error,
  patientId,
  patient,
  onReadingAdded,
  onDateRangeChange,
}) => {
  const location = useLocation();
  const showManualAdd = useMemo(() => {
    return new URLSearchParams(location.search).get("reading") === "true";
  }, [location.search]);

  const { profile, user, hasModuleAdminAccess } = useAuth();
  const isAdmin = hasModuleAdminAccess("canAccessPatients");
  const isCaretakerWithFacility = profile?.type === "facility" && user?.role === "caretaker";
  const isCaretakerAssignedToPatient = useMemo(() => {
    if (!isCaretakerWithFacility) return true;
    const assignedCaretakerIds = (patient?.assignedCaretakerIds || []).map((v: any) => typeof v === "string" ? v : v?._id);
    const profileId = profile?._id;
    if (!profileId) return false;
    return assignedCaretakerIds.includes(profileId);
  }, [patient?.assignedCaretakerIds, profile?._id, isCaretakerWithFacility]);
  const canShowTakeReading = isAdmin || (isCaretakerWithFacility && isCaretakerAssignedToPatient);

  const [datePreset, setDatePreset] = useState<ReportDatePreset>("last30");
  const [customRange, setCustomRange] = useState<DateRange | undefined>();
  const [draftCustomRange, setDraftCustomRange] = useState<DateRange | undefined>();
  const [customPickerOpen, setCustomPickerOpen] = useState(false);
  const [activeRangeBoundary, setActiveRangeBoundary] = useState<"from" | "to" | null>(null);
  const isFutureCalendarDay = (d: Date) =>
    isAfter(startOfDay(d), startOfDay(new Date()));

  const handleCustomRangeSelect = (_next: DateRange | undefined, day?: Date) => {
    if (!day || isFutureCalendarDay(day)) return;
    setDraftCustomRange((prev) => {
      const from = prev?.from;
      const to = prev?.to;

      if (!from || !to) {
        if (from && !to) {
          setActiveRangeBoundary(null);
          return day < from ? { from: day, to: from } : { from, to: day };
        }
        setActiveRangeBoundary("to");
        return { from: day, to: undefined };
      }

      if (day.toDateString() === from.toDateString()) {
        setActiveRangeBoundary("from");
        return prev;
      }
      if (day.toDateString() === to.toDateString()) {
        setActiveRangeBoundary("to");
        return prev;
      }

      if (activeRangeBoundary === "from") {
        setActiveRangeBoundary(null);
        return day <= to ? { from: day, to } : { from: to, to: day };
      }
      if (activeRangeBoundary === "to") {
        setActiveRangeBoundary(null);
        return day >= from ? { from, to: day } : { from: day, to: from };
      }

      setActiveRangeBoundary("to");
      return { from: day, to: undefined };
    });
  };

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isTakeReadingOpen, setIsTakeReadingOpen] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    systolic: "",
    diastolic: "",
    heartRate: "",
  });

  // Fetch device ID for this patient and device type
  useEffect(() => {
    const fetchDeviceId = async () => {
      if (!patientId) return;
      try {
        const response = await AxiosInstance().get(`/devices?patientId=${patientId}`);
        const devices = ((response.data as any)?.data?.devices) || [];
        const matchingDevice = devices.find((device: any) => 
          device.deviceType === "blood_pressure"
        );
        if (matchingDevice?._id) {
          setDeviceId(matchingDevice._id);
        }
      } catch (error) {
        console.error("Error fetching deviceId:", error);
      }
    };
    fetchDeviceId();
  }, [patientId]);

  const { startDate, endDate } = useMemo(
    () =>
      getReportDateRange(
        datePreset,
        customRange?.from,
        customRange?.to ?? customRange?.from,
      ),
    [datePreset, customRange],
  );

  useEffect(() => {
    onDateRangeChange?.({ startDate, endDate });
  }, [startDate, endDate, onDateRangeChange]);

  const chartGranularity = useMemo<"day" | "month" | "year">(() => {
    if (datePreset === "today" || datePreset === "yesterday") return "day";
    if (datePreset === "last7" || datePreset === "last15" || datePreset === "last30")
      return "month";
    return "year";
  }, [datePreset]);

  // Filter readings based on selected date range
  const filteredReadings = useMemo(() => {
    if (!readings.length) return [];
    const rangeStart = moment(startDate, "YYYY-MM-DD").startOf("day");
    const rangeEnd = moment(endDate, "YYYY-MM-DD").endOf("day");
    return readings
      .filter((reading) => {
        const readingDate = moment(reading.readingDateLocal || reading.readingDate);
        return readingDate.isBetween(rangeStart, rangeEnd, undefined, "[]");
      })
      .sort(
        (a, b) =>
          moment(a.readingDateLocal || a.readingDate).valueOf() -
          moment(b.readingDateLocal || b.readingDate).valueOf(),
      );
  }, [readings, startDate, endDate]);

  const tableReadings = useMemo(() => {
    return [...filteredReadings].sort((a, b) => {
      return moment(b.readingDateLocal || b.readingDate).valueOf() - moment(a.readingDateLocal || a.readingDate).valueOf();
    });
  }, [filteredReadings]);

  const bpTableViewData = useMemo(
    () =>
      tableReadings.map((reading) => ({
        ...reading,
        date: formatDateTime12(
          reading.readingDateLocal || reading.readingDate,
        ),
        bpReading:
          reading.systolic !== undefined
            ? `${reading.systolic}/${reading.diastolic}`
            : "-",
        pulse:
          reading.pulse !== undefined ? `${reading.pulse} BPM` : "-",
      })),
    [tableReadings],
  );

  // Prepare chart data
  const chartData = useMemo(() => {
    return filteredReadings.map((reading) => ({
      date: moment(reading.readingDateLocal || reading.readingDate).format(
        chartGranularity === "day"
          ? "hh:mm A"
          : chartGranularity === "month"
          ? "MMM DD"
          : "MMM YYYY"
      ),
      systolic: reading.systolic || 0,
      diastolic: reading.diastolic || 0,
      pulse: reading.pulse || 0,
      fullDate: reading.readingDateLocal || reading.readingDate,
    }));
  }, [filteredReadings, chartGranularity]);

  const chartConfig = {
    systolic: {
      label: "Systolic",
      color: "#3b82f6", // Blue
    },
    diastolic: {
      label: "Diastolic",
      color: "#ef4444", // Red
    },
  };

  const handleAddReading = async () => {
    if (!formData.systolic || !formData.diastolic) {
      toast.error("Please fill in Systolic and Diastolic values");
      return;
    }

    if (!patientId) {
      toast.error("Patient ID is missing");
      return;
    }

    const { pr, rejectFn, resolveFn } = getPromise();
    getPromiseToast(pr);

    try {
      setBtnLoading(true);
      const payload: any = {
        patientId,
        systolic: parseInt(formData.systolic),
        diastolic: parseInt(formData.diastolic),
      };

      if (formData.heartRate) {
        payload.pulse = parseInt(formData.heartRate);
      }

      await AxiosInstance().post("/reading/manual/blood-pressure", payload);
      resolveFn("Blood pressure reading added successfully");
      setIsDialogOpen(false);
      setFormData({ systolic: "", diastolic: "", heartRate: "" });
      if (onReadingAdded) {
        onReadingAdded();
      }
    } catch (error: any) {
      rejectFn(error);
      toast.error(
        error?.response?.data?.message || "Failed to add reading"
      );
    } finally {
      setBtnLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="py-8 text-center">
        <p className="text-sm text-tertiary">Loading readings...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8 text-center">
        <p className="text-sm text-destructive">Error: {error}</p>
      </div>
    );
  }

  if (readings.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row items-start gap-1.5 md:gap-0 md:items-center justify-between">
          <h3 className="text-lg font-semibold text-tertiary">Blood Pressure Trend</h3>
          <div className="flex w-full md:w-fit items-center md:justify-start justify-end gap-4">
            {canShowTakeReading && (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => setIsTakeReadingOpen(true)}
                >
                  Take Reading
                </Button>
                {showManualAdd && (
                  <Button variant="outline" onClick={() => setIsDialogOpen(true)}>
                    Add Reading Manually
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
        <div className="py-8 text-center">
          <p className="text-sm text-tertiary">
            No blood pressure readings available.
          </p>
        </div>

        {/* Add Reading Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add Blood Pressure Reading</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="systolic">
                  Systolic <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="systolic"
                  type="number"
                  placeholder="Enter systolic value"
                  value={formData.systolic}
                  onChange={(e) =>
                    setFormData({ ...formData, systolic: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="diastolic">
                  Diastolic <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="diastolic"
                  type="number"
                  placeholder="Enter diastolic value"
                  value={formData.diastolic}
                  onChange={(e) =>
                    setFormData({ ...formData, diastolic: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="heartRate">
                  Heart Rate <span className="text-muted-foreground">Optional</span>
                </Label>
                <Input
                  id="heartRate"
                  type="number"
                  placeholder="Enter heart rate"
                  value={formData.heartRate}
                  onChange={(e) =>
                    setFormData({ ...formData, heartRate: e.target.value })
                  }
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsDialogOpen(false);
                    setFormData({ systolic: "", diastolic: "", heartRate: "" });
                  }}
                >
                  Cancel
                </Button>
                <Button
                  disabled={btnLoading || !formData.systolic || !formData.diastolic}
                  onClick={handleAddReading}
                >
                  Confirm
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Take Reading Popup */}
        {patientId && (
          <TakeReadingPopup
            isOpen={isTakeReadingOpen}
            onClose={() => setIsTakeReadingOpen(false)}
            patientId={patientId}
            deviceType="blood_pressure"
            deviceId={deviceId}
            onReadingReceived={() => {
              if (onReadingAdded) {
                onReadingAdded();
              }
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Chart Section */}
      <div className="space-y-4">
        <div className="flex flex-col md:flex-row items-start gap-1.5 md:gap-0 md:items-center justify-between">
          <h3 className="text-lg font-semibold text-tertiary">Blood Pressure Trend</h3>
          <div className="flex w-full md:w-fit items-center md:justify-start justify-end gap-4">
            <div className="flex items-center gap-2">
              <Select
                value={datePreset}
                onValueChange={(v) => {
                  const preset = v as ReportDatePreset;
                  setDatePreset(preset);
                  if (preset !== "custom") {
                    setCustomPickerOpen(false);
                  } else {
                    setDraftCustomRange(customRange);
                    setActiveRangeBoundary(null);
                    setCustomPickerOpen(true);
                  }
                }}
              >
                <SelectTrigger className="h-9 w-[min(200px,42vw)] shrink-0 rounded-lg border-gray-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRESET_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {datePreset === "custom" && (
                <Popover
                  open={customPickerOpen}
                  onOpenChange={(open) => {
                    setCustomPickerOpen(open);
                    if (open) {
                      setDraftCustomRange(customRange);
                      setActiveRangeBoundary(null);
                      return;
                    }
                    setCustomRange(draftCustomRange);
                    setActiveRangeBoundary(null);
                  }}
                >
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="h-9 shrink-0 rounded-lg whitespace-nowrap">
                      <CalendarDays className="mr-2 h-4 w-4" />
                      {draftCustomRange?.from && draftCustomRange?.to
                        ? `${draftCustomRange.from.toLocaleDateString()} — ${draftCustomRange.to.toLocaleDateString()}`
                        : draftCustomRange?.from
                          ? `${draftCustomRange.from.toLocaleDateString()} — …`
                          : "Pick dates"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="range"
                      numberOfMonths={2}
                      selected={draftCustomRange}
                      onSelect={handleCustomRangeSelect as any}
                      defaultMonth={draftCustomRange?.from}
                      disabled={(date) => isFutureCalendarDay(date)}
                    />
                  </PopoverContent>
                </Popover>
              )}
            </div>
            <div className="flex items-center gap-2">
              {canShowTakeReading && (
                <>
                  <Button 
                    variant="outline" 
                    onClick={() => setIsTakeReadingOpen(true)}
                  >
                    Take Reading
                  </Button>
                  {showManualAdd && (
                    <Button variant="outline" onClick={() => setIsDialogOpen(true)}>
                      Add Reading Manually
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {chartData.length > 0 ? (
          <div className="border border-grey rounded-lg p-4 bg-white">
            <ChartContainer config={chartConfig} className="h-[300px] w-full">
              <LineChart
                data={chartData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="date"
                  stroke="#6b7280"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  stroke="#6b7280"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  label={{ value: "mmHg", angle: -90, position: "insideLeft" }}
                />
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      labelFormatter={(value, payload) => {
                        if (payload && payload[0]) {
                          const data = payload[0].payload;
                          return moment(data.fullDate).format("MMM DD, YYYY hh:mm A");
                        }
                        return value;
                      }}
                    />
                  }
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="systolic"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  name="Systolic"
                />
                <Line
                  type="monotone"
                  dataKey="diastolic"
                  stroke="#ef4444"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  name="Diastolic"
                />
              </LineChart>
            </ChartContainer>
          </div>
        ) : (
          <div className="border border-grey rounded-lg p-8 bg-white text-center">
            <p className="text-sm text-tertiary">
              No readings available for the selected time period.
            </p>
          </div>
        )}
      </div>

      {/* Table Section */}
      {tableReadings.length > 0 && (
        <>
        {/* <div className="overflow-x-auto">
          <Table>
        <TableHeader>
          <TableRow className="bg-grey-light hover:bg-grey-light">
            <TableHead className="font-semibold text-tertiary text-sm">
              Date
            </TableHead>
            <TableHead className="font-semibold text-tertiary text-sm">
              BP Reading (Systolic/Diastolic)
            </TableHead>
            <TableHead className="font-semibold text-tertiary text-sm">
              Pulse
            </TableHead>
            <TableHead className="font-semibold text-tertiary text-sm">
              Status
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tableReadings.map((reading) => (
            <TableRow key={reading._id}>
              <TableCell className="text-sm text-tertiary">
                {formatDateTime12(reading.readingDateLocal || reading.readingDate)}
              </TableCell>
              <TableCell className="text-sm text-tertiary">
                {reading.systolic
                  ? `${reading.systolic}/${reading.diastolic}`
                  : "-"}
              </TableCell>
              <TableCell className="text-sm text-tertiary">
                {reading.pulse || "-"}
              </TableCell>
              <TableCell className="text-sm text-tertiary">
                <span
                  className={`px-2 py-1 rounded-full text-xs ${
                    reading.isAbnormal
                      ? "bg-red-100 text-red-800"
                      : "bg-green-100 text-green-800"
                  }`}
                >
                  {reading.status?.replace(/_/g, " ")}
                </span>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
        </div>
        
        <Separator /> */}

        <ClientPaginatedMyTable
                  columnHeaders={[
                    { id: "date", label: "Date", isSortable: false },
                    {
                      id: "bpReading",
                      label: "BP Reading (Systolic/Diastolic)",
                      isSortable: false,
                    },
                    { id: "pulse", label: "Pulse (BPM)", isSortable: false },
                  ]}
                  columnsData={tableReadings}
                  viewColumnData={bpTableViewData}
                  loading={false}
                />
        
        
        </>
      )}

      {/* Add Reading Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Blood Pressure Reading</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="systolic">
                Systolic <span className="text-destructive">*</span>
              </Label>
              <Input
                id="systolic"
                type="number"
                placeholder="Enter systolic value"
                value={formData.systolic}
                onChange={(e) =>
                  setFormData({ ...formData, systolic: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="diastolic">
                Diastolic <span className="text-destructive">*</span>
              </Label>
              <Input
                id="diastolic"
                type="number"
                placeholder="Enter diastolic value"
                value={formData.diastolic}
                onChange={(e) =>
                  setFormData({ ...formData, diastolic: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="heartRate">
                Heart Rate <span className="text-muted-foreground">Optional</span>
              </Label>
              <Input
                id="heartRate"
                type="number"
                placeholder="Enter heart rate"
                value={formData.heartRate}
                onChange={(e) =>
                  setFormData({ ...formData, heartRate: e.target.value })
                }
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsDialogOpen(false);
                  setFormData({ systolic: "", diastolic: "", heartRate: "" });
                }}
              >
                Cancel
              </Button>
              <Button
                disabled={btnLoading || !formData.systolic || !formData.diastolic}
                onClick={handleAddReading}
              >
                Confirm
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Take Reading Popup */}
      {patientId && (
        <TakeReadingPopup
          isOpen={isTakeReadingOpen}
          onClose={() => setIsTakeReadingOpen(false)}
          patientId={patientId}
          deviceType="blood_pressure"
          deviceId={deviceId}
          onReadingReceived={() => {
            if (onReadingAdded) {
              onReadingAdded();
            }
          }}
        />
      )}
    </div>
  );
};

export default BloodPressureReadings;

